/**
 * An environment is one of three pre-defined deployment stages commonly used at RTL.
 *
 * @remarks
 * The player needs to know the environment to call the correct remote config endpoint.
 *
 * Because the environment value is expected to be well known and used across all apps,
 * we also used to determine the correct analytics account.
 *
 * For the same reason we also use it in the error reporting plugin to identify the environment.
 *
 * It is crucial that the environment value is set correctly,
 * so we send the correct data to the correct endpoints.
 */
export const Environment = {
  /**
   * Production environment.
   *
   * @remarks
   * If you don't specify an environment, the player will use this one.
   *
   * We assume that an unspecified environment is production, because we don't want to accidentally
   * lose valuable data like errors or analytics in general.
   *
   * On the other hand a client should make sure that the environment is set correctly,
   * especially for testing purposes. In general think about it like this:
   * "Should this be tracked in production?" If the answer is no, then use a different environment.
   */
  Production: 'prod',

  /**
   * Pre-production environment.
   *
   * @remarks
   * Commonly also referred to as staging or "PreProd".
   *
   * Use this for testing purposes before releasing to production. The pre-production environment
   * should mirror the production environment as closely as possible.
   */
  PreProduction: 'preprod',

  /**
   * Development environment.
   *
   * @remarks
   * Commonly also referred to as "dev". Mostly used for local development or our QA environment.
   *
   * Use this to test new features. If you want to test a bugfix it may be desirable to use the
   * Production or PreProduction environment instead, to test with real settings or rather more realistic settings.
   */
  Development: 'dev',
} as const;

/**
 * {@inheritDoc Environment:var}
 */
export type Environment = (typeof Environment)[keyof typeof Environment];
